import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import axios from "@common/axios";
import {
    Button,
    Form,
    Alert,
    Row,
    Col,
    Space,
    Modal,
    Card,
    Spin,
    message,
    Tooltip
} from 'antd';
import { UserContext } from "@context/UserContext";
import { BrandContext } from "@context/BrandContext";
import { useCloseTest, useUpdateTestResults, useSubmitTest } from './useStudentTest';
import TestQuestion from "./TestQuestion";
import CircularLoader from "@common/CircularLoader";
import TestHeader from "./TestHeader";
import TestCountdown from "./TestCountdown";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import SubmitTestConfirm from "./SubmitTestConfirm";
import {ContentContext} from "@context/ContentContext";
import ButtonsGroup from "@common/ButtonsGroup";

const DiagnosticTest = () => {
    const { user } = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const {setContentLoading} = useContext(ContentContext);
    const testRef = useRef();

    const [testAssignmentId, setTestAssignmentId] = useState();
    const [test, setTest] = useState();
    const [testResult, setTestResult] = useState();
    const [questionResults, setQuestionResults] = useState([]);
    const [testAssignmentResult, setTestAssignmentResult] = useState();
    const [isExpired, setExpired] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [questionLoading, setQuestionLoading] = useState(true);
    const [error, setError] = useState();
    const [currentQuestion, setCurrentQuestion] = useState();
    const [currentQuestionResult, setCurrentQuestionResult] = useState();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState();
    const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
    const [skipButtonDisabled, setSkipButtonDisabled] = useState(true);
    const [showSubmitTestConfirm, setShowSubmitTestConfirm] = useState(false);
    const [lastEnabledQuestionIndex, setLastEnabledQuestionIndex] = useState(false);
    const [form] = Form.useForm();
    const { loadShortcutByUrl } = useCurrentShortcut();
    const { isUpdating, updateTestResults } = useUpdateTestResults({
        testRef,
        testResult,
        onError: setError,
    });
    const { isClosing, closeTest } = useCloseTest({
        testRef,
        testResult,
        loadShortcutByUrl,
        onError: setError,
    });
    const { isSubmitting, submitTest } = useSubmitTest({
        testResult,
        setTestAssignmentResult,
        setSubmitted,
        loadShortcutByUrl,
        onError: setError,
        displayToastMessage: displayToastMessage
    });

    const fetchData = useCallback((testAssignmentId) => {
        setLoading(true);
        setError(undefined);
        axios.get('/web/ui/test/startTest', {
            params: { testAssignmentId }
        }).then((response) => {
            const data = response.data;
            setTest(data.test);
            setTestResult(data.testResult);
            setQuestionResults(data.questionResults);

            if(data.test?.total > 0) {
                if(data.questionResults == undefined) {
                    setCurrentQuestionIndex(0);
                } else {
                    const firstUnsubmittedIndex = data.questionResults.findIndex(questionResult => questionResult.attempted === false);
                    let enableQuestionIndex = firstUnsubmittedIndex;
                    if(firstUnsubmittedIndex === -1) {
                        if(data.questionResults.length < data.test.total - 1) {
                            enableQuestionIndex = (data.questionResults.length);
                        } else {
                            enableQuestionIndex = data.questionResults.length - 1;
                        }
                    }
                    setCurrentQuestionIndex(enableQuestionIndex);
                }
            }
        }).catch((error) => {
            setError('Unable to load test. Please try again.' + (error.message || ''));
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1);
            const hashNumericValue = Number(hash);
            if (!isNaN(hashNumericValue) && typeof hashNumericValue === 'number') {
                setTestAssignmentId(hashNumericValue);
            } else {
                setTestAssignmentId(undefined);
            }
        };
        handleHashChange();

        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    useEffect(() => {
        if (testAssignmentId == undefined) {
            return;
        }
        fetchData(testAssignmentId);
    }, [testAssignmentId]);

    useEffect(() => {
        let disableSkipButton = false;
        let disableNextButton = false;

        if(currentQuestionResult == undefined) {
            disableNextButton = true;
        } else if(test.adaptive && currentQuestionResult?.marked) {
            if (currentQuestionIndex == test.total - 1) {
                disableNextButton = true;
            }
            disableSkipButton = true;
        } else if (currentQuestionResult?.unsubmitted) {
            if (currentQuestionResult?.hasUserResponse) {
                disableNextButton = false;
            } else {
                disableNextButton = true;
            }
        }

        setSkipButtonDisabled(disableSkipButton);
        setNextButtonDisabled(disableNextButton);
    }, [currentQuestionResult, test]);

    useEffect(() => {
        const updateTestQuestionTime = () => {
            if (testAssignmentId == undefined) {
                return;
            }
            if(testRef.current == undefined) {
                return;
            }

            const questionId = testRef.current.querySelector('.js-testContent .js-question')?.dataset.questionId;
            if(questionId == undefined) {
                return;
            }

            axios.post('/web/ui/test/testQuestion/updateTime', undefined, {params: { testAssignmentId, questionId }})
                .catch((error) => {
                    console.error('Error notifying backend on unload:', error);
                });
        };

        window.addEventListener('beforeunload', updateTestQuestionTime);
        document.addEventListener('ch-bodyChange', updateTestQuestionTime);

        return () => {
            window.removeEventListener('beforeunload', updateTestQuestionTime);
            document.removeEventListener('ch-bodyChange', updateTestQuestionTime);
        };
    }, [testAssignmentId]);

    const loadQuestionResult = (questionId, testAssignmentId) => {
        if (testAssignmentId == undefined || questionId == undefined) {
            return;
        }

        setError(undefined);
        setQuestionLoading(true);
        axios.get('/web/ui/test/testResult/question', {params: {questionId, testAssignmentId}})
            .then((response) => {
                const data = response.data;
                setCurrentQuestionResult(data.questionResult);

                if (data.testResult) {
                    setTestResult(data.testResult);
                    setQuestionResults(data.testResult.testQuestionResults);
                }

                if(data.questionResult) {
                    const question = testRef.current.querySelector(`.js-question[data-question-id="${questionId}"]`);
                    if (data.questionResult != undefined && question != undefined) {
                        if(data.questionResult.showWorking) {
                            inlineQuestions.insertQuestionWorking(question, data.questionResult);
                        }
                        const readOnly = !data.questionResult.unsubmitted && test.adaptive;
                        inlineQuestions.insertAnswers($(question), data.questionResult, false, false, readOnly);
                    }
                }
        }).catch((error) => {
            console.error('Error loading question result', error);
            setError('Error loading question result');
        }).finally(() => {
            setQuestionLoading(false);
        });
    };

    const loadNextQuestion = (questionId, testAssignmentId) => {
        if (testAssignmentId == undefined) {
            return;
        }

        setError(undefined);
        setQuestionLoading(true);
        axios.get('/web/ui/test/testQuestion/next', {params: {questionId, testAssignmentId}})
            .then((response) => {
                const data = response.data;
                setCurrentQuestion(data.question);
                setTestResult(data.testResult);
            }).catch((error) => {
                console.error('Error loading question result', error);
                setError('Error loading question result');
            }).finally(() => {
                setQuestionLoading(false);
            });
    };

    const loadQuestion = (questionId, testAssignmentId) => {
        if (questionId == undefined || testAssignmentId == undefined) {
            return;
        }

        setError(undefined);
        setQuestionLoading(true);
        axios.get('/web/ui/test/testQuestion/question', {params: {questionId, testAssignmentId}})
            .then((response) => {
                const data = response.data;
                setCurrentQuestion(data.question);
            }).catch((error) => {
                console.error('Error loading test question', error);
                setError('Error loading test question');
            }).finally(() => {
                setQuestionLoading(false);
            });
    };

    const skipQuestion = (questionId, testAssignmentId, callback) => {
        if(questionId == undefined || testAssignmentId == undefined) {
            return;
        }

        setError(undefined);
        setQuestionLoading(true);
        axios.post('/web/ui/test/testQuestion/skip', undefined, {params: {questionId: questionId, testAssignmentId: testAssignmentId}})
            .then((response) => {
                const data = response.data;
                if(callback) {
                    callback(data);
                }
            })
            .catch((error) => {
                console.error('Skip question error', error);
                setError('There was an error skipping the question. Please try again.');
            }).finally(() => {
                setQuestionLoading(false);
            });
    };

    const handleNextQuestion = () => {
        if(test == undefined || currentQuestionIndex == undefined) {
            return;
        }

        if(currentQuestion == undefined) {
            return;
        }

        if(currentQuestionResult == undefined || currentQuestionResult.unsubmitted || !test.adaptive) {
            setNextButtonDisabled(true);
            updateTestResults({questionId: currentQuestion.questionId, submitAnswers: true, onComplete: () => {
                if (currentQuestionIndex >= 0 && currentQuestionIndex < test.total - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex => currentQuestionIndex + 1);
                } else {
                    loadQuestionResult(currentQuestion.questionId, test.testAssignmentId);
                }
            }});
        } else {
            if (currentQuestionIndex >= 0 && currentQuestionIndex < test.total - 1) {
                setCurrentQuestionIndex(currentQuestionIndex => currentQuestionIndex + 1);
            }
        }
    };

    const handlePreviousQuestion = () => {
        if(test == undefined || currentQuestionIndex == undefined) {
            return;
        }

        if (currentQuestionIndex <= 1 || currentQuestionIndex >= test.total) {
            return;
        }

        if(currentQuestion == undefined) {
            return;
        }

        if(currentQuestionResult?.unsubmitted) {
            updateTestResults({questionId: currentQuestion.questionId, submitAnswers: true, onComplete: () => {
                setCurrentQuestionIndex(currentQuestionIndex => currentQuestionIndex - 1);
            }});
        } else {
            setCurrentQuestionIndex(currentQuestionIndex => currentQuestionIndex - 1);
        }
    };

    const handleTestExpired = () => {
        loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
        setExpired(false);
        Modal.destroyAll();
    };

    useEffect(() => {
        if (test == undefined || currentQuestionIndex == undefined) {
            return;
        }

        if (currentQuestionIndex < 0 || currentQuestionIndex >= test.total) {
            return;
        }

        if(test.adaptive) {
            if(questionResults == undefined || currentQuestionIndex >= questionResults.length) {
                loadNextQuestion(currentQuestion?.questionId, test.testAssignmentId);
            } else {
                if(currentQuestionIndex < questionResults.length) {
                    loadQuestion(questionResults[currentQuestionIndex].questionId, test.testAssignmentId);
                }
            }

        } else {
            if(test.testQuestionBeans && currentQuestionIndex >= 0 && currentQuestionIndex < test.testQuestionBeans.length) {
                setCurrentQuestion(test.testQuestionBeans[currentQuestionIndex]);
            }
        }
    }, [currentQuestionIndex, test]);

    useEffect(() => {
        if(currentQuestion == undefined || test == undefined) {
            return;
        }
        loadQuestionResult(currentQuestion.questionId, test.testAssignmentId);
    }, [currentQuestion]);

    useEffect(() => {
        if(test == undefined || questionResults == undefined) {
            return;
        }

        const firstUnsubmittedIndex = questionResults.findIndex(questionResult => questionResult.attempted === false);
        let enableQuestionIndex = firstUnsubmittedIndex;
        if (firstUnsubmittedIndex === -1) {
            if (questionResults.length < test.total - 1) {
                enableQuestionIndex = (questionResults.length);
            } else {
                enableQuestionIndex = questionResults.length - 1;
            }
        }

        setLastEnabledQuestionIndex(enableQuestionIndex);
    }, [questionResults, test]);

    useEffect(() => {
        if(isExpired) {
            Modal.confirm({
                title: brandConfig.testName + ' Expired',
                content: <p className="ch-warning-text" style={{textAlign: 'left'}}>Your {brandConfig.testName.toLowerCase()} has expired.</p>,
                okCancel: false,
                footer: () => (
                    <ButtonsGroup position={'right'}>
                        <Button className={'ant'} type="primary" onClick={handleTestExpired}>
                            Return to {brandConfig.testsName}
                        </Button>
                    </ButtonsGroup>
                )
            });
        }
    }, [isExpired]);

    const handleSkipQuestion = () => {
        if(currentQuestion == undefined || test == undefined) {
            return;
        }

        skipQuestion(currentQuestion.questionId, test.testAssignmentId, (data) => {
            setTestResult(data.testResult);
            if(data.questionResult != undefined && questionResults != undefined) {
                setQuestionResults((prevResults) => {
                    const newResults = [...prevResults];

                    if (currentQuestionIndex < newResults.length) {
                        newResults[currentQuestionIndex] = data.questionResult;
                    } else {
                        newResults.push(data.questionResult);
                    }

                    return newResults;
                });
            }
            setCurrentQuestionIndex(currentQuestionIndex => currentQuestionIndex + 1);
        });
    };

    const handleSubmitTest = () => {
        if(test == undefined) {
            return;
        }
        setShowSubmitTestConfirm(true);
    }

    const handleCloseTest = () => {
        if (testAssignmentId == undefined) {
            console.error('testAssignmentId is undefined.');
            return;
        }

        const handleOk = () => {
            if (testRef?.current) {
                $('.js-testContent', testRef.current).prop('disabled', true);
            }
            updateTestResults({submitAnswers: true, onComplete: () => {
                Modal.destroyAll();
                closeTest();
            }});
        };

        if (testResult.testType === 'ASSESSMENT') {
            Modal.confirm({
                title: 'Exit test',
                content: (
                    <div>
                        <p>Closing this test will mean that your result will be recorded as not attempted.</p>
                        <p>Do you wish to continue?</p>
                    </div>
                ),
                width: 500,
                footer: () => (
                    <ButtonsGroup position={'right'}>
                        <Button className={'ant'} onClick={() => Modal.destroyAll()}>
                            No
                        </Button>
                        <Button className={'ant'} type="primary" onClick={handleOk}>
                            Yes
                        </Button>
                    </ButtonsGroup>
                )
            });
        } else {
            updateTestResults({submitAnswers: true, onComplete: () => {
                if (testResult.testType === 'QUICK') {
                    loadShortcutByUrl('/controlCentre/showStudentTestsTabbed.action');
                } else {
                    loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
                }
            }});
        }
    };

    const handleExpiryTest = () => {
        setExpired(true);
    };

    const handleNavigateToQuestion = (index) => {
        if(index < 0 || index >= test.total) {
            return;
        }

        if(currentQuestion != undefined && (!test.adaptive || currentQuestion.unsubmitted)) {
            updateTestResults({questionId: currentQuestion.questionId, submitAnswers: true, onComplete: () => {
                    setCurrentQuestionIndex(index);
                }});
        } else {
            setCurrentQuestionIndex(index);
        }
    };

    const handleQuestionInputChange = (event) => {
        if (currentQuestion == undefined) {
            return;
        }
        if(currentQuestionResult != undefined && !currentQuestionResult.unsubmitted) {
            return;
        }
        if(event.target.value == '') {
            //setNextButtonDisabled(true);
        } else {
            setNextButtonDisabled(false);
        }
    };

    const handleSubmitConfirmClose = () => {
        setShowSubmitTestConfirm(false);
        if(testAssignmentResult != undefined) {
            loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
        }
    }

    if (loading) {
        return <CircularLoader />;
    }

    if (test == undefined) {
        displayToastMessage('Your assessment may be complete, expired or invalid.', 'error');
        loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
    }

    if (testResult == undefined) {
        return <Space className="ch-error-blk" orientation="vertical" align="center">
            <Alert type="error" message="This assessment is not available." description="This assessment has been completed and is no longer available."/>
            <Button type={'primary'} onClick={() => loadShortcutByUrl('/web/ui/view/test/studentTestAssignments')}>Return to your {brandConfig.testsName}</Button>
        </Space>;
    }

    const isProcessing = isUpdating || isClosing || isSubmitting;
    const questionIndexes = test ? [...Array(test.total).keys()] : [];

    return (
        <div>
            {error && <Alert type="error" message={error} />}
            <SubmitTestConfirm open={showSubmitTestConfirm} isLoading={isProcessing} onClose={handleSubmitConfirmClose} questionsRemaining={test.total - (testResult?.totalAnswered || 0) - (testResult?.totalSkipped || 0)} testAssignmentResult={testAssignmentResult} submitTest={submitTest} updateTestResults={updateTestResults}/>
            <TestHeader test={test} testResult={testResult} />
            <div
                className="pw-card-padd js-test js-reactTest"
                data-test-id={test.testId}
                data-test-type={test.testType}
                data-test-purpose={test.testPurpose}
                data-test-status={testResult.testAssignmentStatus}
                data-test-assignment-id={testResult.testAssignmentId}
                data-step-id={test.stepId}
                data-diagnostic={test.diagnosticTest}
                data-adaptive={test.adaptive}
                data-show-results={false}
                data-test-submitted={isSubmitted}
                data-test-expired={isExpired}
                ref={testRef}
            >
                <Row justify="center" className="margin-b--m">
                    <Space>
                        <Button type="primary" onClick={handleSubmitTest}>
                            Submit
                        </Button>
                        <Button type="primary" onClick={handleCloseTest}>
                            {test.testType === 'ASSESSMENT' ? 'Exit' : 'Close'}
                        </Button>
                    </Space>
                </Row>
                <Card>
                    <Row justify="start" style={{paddingBottom: 8}}>
                        <Space>
                            {questionIndexes.map((_, index) => {
                                let className = '';
                                let questionStatus = 'Unanswered';

                                if(questionResults != undefined && index < questionResults.length && questionResults[index].skipped) {
                                    className = 'ch-skipped-indicator';
                                    questionStatus = 'Skipped';
                                } else if(questionResults != undefined && index < questionResults.length && questionResults[index].hasUserResponse) {
                                    className = 'ch-complete-indicator';
                                    questionStatus = 'Answered';
                                }

                                if (currentQuestion != undefined && currentQuestionIndex != undefined && currentQuestionIndex === index) {
                                    className = 'ch-selected-indicator ' + className
                                }

                                return (
                                    <Tooltip
                                        key={index}
                                        title={questionStatus}
                                    >
                                        <Button
                                            shape="circle"
                                            className={className}
                                            style={{ width: 36, minWidth: 36, height: 36, padding: 0 }}
                                            disabled={questionResults == undefined  || isUpdating || index >= questionResults.length || index > lastEnabledQuestionIndex}
                                            onClick={() => handleNavigateToQuestion(index)}
                                        >
                                            {index + 1}
                                        </Button>
                                    </Tooltip>
                                );
                            })}
                        </Space>
                    </Row>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <strong>
                                <>{testResult?.totalAnswered || 0} answered</>
                                {testResult?.totalSkipped > 0 &&
                                    <>, {testResult?.totalSkipped} skipped </>
                                }
                                <> of {testResult?.total} questions</>
                            </strong>
                        </Col>
                        <Col>
                            {testResult.endDate && (
                                <>
                                    {isExpired ? (
                                        <span className="ch-error-text">Expired</span>
                                    ) : (
                                        <TestCountdown endTime={testResult.endDate} onExpire={handleExpiryTest}/>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                    <Spin spinning={isSubmitted || isExpired}>
                        <div className="js-testContent">
                            <Form
                                form={form}
                                name={`testForm`}
                                layout="vertical"
                                className="js-testQuestionPage"
                            >
                                {currentQuestion ?
                                    <div
                                        key={currentQuestion.questionId}
                                        data-question={currentQuestion.questionId}
                                        data-question-has-input={false}
                                    >
                                        <TestQuestion
                                            testQuestion={currentQuestion}
                                            questionNumber={(currentQuestionIndex || 0) + 1}
                                            showExamCitations={!test.hideStudentExamCitations}
                                            tagText={currentQuestionResult?.skipped ? 'Skipped' : currentQuestionResult?.hasUserResponse ? 'Answered' : ''}
                                            onKeyUp={handleQuestionInputChange}
                                            onChange={handleQuestionInputChange}
                                            contentClassName={test.syllabusContentClass ? test.syllabusContentClass : 'edjin-text'}
                                        />
                                    </div> : <CircularLoader displayInElement={true}/>
                                }
                            </Form>
                        </div>

                        <Row className="margin-t--m">
                            <Space>
                                <Button
                                    type="primary"
                                    data-test-assignment-id={testAssignmentId}
                                    disabled={skipButtonDisabled || questionLoading || isUpdating}
                                    onClick={handleSkipQuestion}
                                >
                                    I don't know
                                </Button>
                                <Button
                                    type="primary"
                                    data-test-assignment-id={testAssignmentId}
                                    disabled={nextButtonDisabled || isUpdating}
                                    onClick={handleNextQuestion}
                                >
                                    {currentQuestionResult != undefined && currentQuestionResult.marked && test?.adaptive ?
                                        <>Next</> :
                                        <>{currentQuestionIndex == (test.total - 1) ? <>Save</> : <>Save and Next</>}</>
                                    }
                                </Button>
                                <CircularLoader visible={questionLoading || isUpdating} pageCentered={false} displayInElement={true}/>
                            </Space>
                        </Row>
                    </Spin>
                </Card>
                <Row justify="center" className="margin-t--m">
                    <Space>
                        <Button type="primary" onClick={handleSubmitTest}>
                            Submit
                        </Button>
                        <Button type="primary" onClick={handleCloseTest}>
                            {test.testType === 'ASSESSMENT' ? 'Exit' : 'Close'}
                        </Button>
                    </Space>
                </Row>
            </div>
        </div>
    );
};

export default DiagnosticTest;
